import React from 'react';
import { Link } from 'react-router-dom';
import { ModeToggle, Footer, useTitle } from '../components/Layout';
import { profile, experiences, education, skills, scenarios } from '../data';

const CONTACTS = [
  { label: 'Résumé (PDF)', href: profile.resume, external: true },
  { label: profile.email, href: `mailto:${profile.email}` },
  { label: 'LinkedIn', href: profile.linkedin, external: true },
  { label: 'Book a call', href: profile.calendly, external: true },
];

function Entry({ title, org, period, location, highlights }) {
  return (
    <article className="cv-entry">
      <div className="cv-entry-head">
        <div>
          <h3 className="cv-entry-title">{title}</h3>
          <p className="cv-entry-org">{org}</p>
        </div>
        <p className="meta">
          {period}
          {location && (
            <>
              <br />
              {location}
            </>
          )}
        </p>
      </div>
      <ul className="bullets">
        {highlights.map((h) => (
          <li key={h}>{h}</li>
        ))}
      </ul>
    </article>
  );
}

export default function Classic() {
  useTitle('Résumé');
  return (
    <div className="shell classic">
      <a href="#main" className="skip-link">Skip to content</a>
      <header className="site-header">
        <div className="header-inner">
          <Link to="/classic" className="brand">
            <span className="brand-name">Rohan Rout</span>
          </Link>
          <div className="header-actions">
            <ModeToggle mode="classic" />
          </div>
        </div>
      </header>

      <main id="main" className="main cv">
        <section className="cv-intro">
          <img src={profile.photo} alt="Portrait of Rohan Rout" className="cv-photo" width="96" height="96" />
          <div>
            <h1 className="cv-name">{profile.name}</h1>
            <p className="cv-role">
              {profile.role} · {profile.focus}
            </p>
          </div>
        </section>
        <p className="cv-bio">{profile.bio}</p>
        <ul className="cv-contacts">
          {CONTACTS.map((c) => (
            <li key={c.label}>
              <a href={c.href} {...(c.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {c.label}
              </a>
            </li>
          ))}
        </ul>

        <section className="cv-section">
          <h2 className="cv-heading">Experience</h2>
          {experiences.map((e) => (
            <Entry key={e.company + e.title} title={e.title} org={e.company} period={e.period} location={e.location} highlights={e.highlights} />
          ))}
        </section>

        <section className="cv-section">
          <h2 className="cv-heading">Education</h2>
          {education.map((e) => (
            <Entry key={e.school} title={e.degree} org={e.school} period={e.period} location={e.location} highlights={e.highlights} />
          ))}
        </section>

        <section className="cv-section">
          <h2 className="cv-heading">Skills</h2>
          <dl className="cv-skills">
            {skills.map((s) => (
              <div key={s.group}>
                <dt>{s.group}</dt>
                <dd>{s.items.join(' · ')}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="cv-section">
          <h2 className="cv-heading">Case studies</h2>
          <div className="cv-cases">
            {scenarios.map((s) => (
              <details key={s.id} className="cv-case">
                <summary>
                  <span className="cv-case-title">{s.title}</span>
                  <span className="cv-case-meta muted">
                    {s.company} · {s.tags.join(', ')}
                  </span>
                  <span className="cv-case-preview">{s.preview}</span>
                </summary>
                <div className="cv-case-body">
                  <h4>Situation</h4>
                  <p>{s.context}</p>
                  {[
                    ['Challenge', s.challenge],
                    ['Approach', s.approach],
                    ['Impact', s.impact],
                  ].map(([label, items]) => (
                    <React.Fragment key={label}>
                      <h4>{label}</h4>
                      <ul className="bullets">
                        {items.map((i) => (
                          <li key={i}>{i}</li>
                        ))}
                      </ul>
                    </React.Fragment>
                  ))}
                  {s.links && (
                    <p className="cv-case-links">
                      {s.links.map((l) => (
                        <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">
                          {l.title} ↗
                        </a>
                      ))}
                    </p>
                  )}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="cv-section cv-cta">
          <h2 className="cv-heading">Let us connect</h2>
          <p>Interested in working together? Have questions about my experience?</p>
          <div className="cta-row">
            <a href={profile.calendly} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Book a call
            </a>
            <a href={`mailto:${profile.email}`} className="btn btn-ghost">
              Email me
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
